/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints, DataPointResponse } from '../DataPoints';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { getCountryName } from '../../../../utils/i18n';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { PercentageCell } from '../../../../views/components/cells/index';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { ASSET_ALLOCATION_GROUPS } from '../../../../utils/constants';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import DonutData from '@/types/donut-data';
import ComparisonItem from '@/types/comparison-item';

/**
 * @class AssetAllocation Data Access Object
 */
export default class EquityCountryAllocationDAO extends ComparisonItemBasedProposalDAO {
  countryInclusions: string[] = [];

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new EquityCountryAllocationDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['EQUITY_COUNTRY_ALLOCATION'];
  }

  // eslint-disable-next-line class-methods-use-this
  private hasEmptyCountries(itemDAO: ComparisonItemDAO) {
    const equityAllocation = DataPoints.getAssetAllocationTotalsByCategory(itemDAO.item, ASSET_ALLOCATION_GROUPS.EQUITY).response;
    return equityAllocation > 0 && itemDAO.item.allocations && itemDAO.item.allocations.equityCountry && itemDAO.item.allocations.equityCountry.length === 0;
  }

  protected build(): void {
    // test for options
    const daoConfig: ProposalNS.EquityCountryAllocationConfig = this.proposalElement.DAO as ProposalNS.EquityCountryAllocationConfig;
    if (daoConfig.countryInclusionConfigDAOOptions !== undefined) {
      this.countryInclusions = daoConfig.countryInclusionConfigDAOOptions.countryInclusions;
    }

    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      const keys: string[] = DataPoints.getEquityCountryAllocations(itemDAO.item);

      let color: string | undefined;

      for (const key of this.countryInclusions) {
        const { response: value, disclaimerKeys }: DataPointResponse<number> = DataPoints.getEquityCountryAllocationByKey(itemDAO.item, key);
        this.addDisclaimerKeys(disclaimerKeys, itemDAO.item);

        const fieldName = getCountryName(key, this.locale);
        if (this.colorsDict && this.colorsDict[key]) {
          color = this.colorsDict[key];
        }

        this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableContent, color);
        const field = new ProposalFieldDAO(fieldName, value || 0, ProposalStyleClassNames.ProposalTableContent);
        field.cellType = PercentageCell;
        field.key = key;
        this.addFieldDAO(itemDAO, field);
      }

      const localItem: ComparisonItem = itemDAO.item;
      const others = keys
        .filter(key => !this.countryInclusions.includes(key))
        .reduce((sum, key) => {
          const { response: value, disclaimerKeys }: DataPointResponse<number> = DataPoints.getEquityCountryAllocationByKey(localItem, key);
          this.addDisclaimerKeys(disclaimerKeys, localItem);

          return sum + (value || 0);
        }, 0);

      const field = new ProposalFieldDAO(
        this.formatMessage('Comparison.Table.AssetDetails.InternationalEquity'),
        others,
        ProposalStyleClassNames.ProposalTableContent,
      );

      if (daoConfig.displayOther) {
        const otherValue = this.hasEmptyCountries(itemDAO) ? 100 : 0;
        const otherField = new ProposalFieldDAO(
          `${this.formatMessage('Comparison.Table.AssetDetails.Other')}*`,
          otherValue,
          ProposalStyleClassNames.ProposalTableContent,
          PercentageCell,
        );
        this.addFieldDAO(itemDAO, otherField);
        this.addFieldName(otherField.label, ProposalStyleClassNames.ProposalTableContent, (this.colorsDict && this.colorsDict.OTHER) || undefined);
        otherField.key = 'OTHER';
        this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
      }

      if (this.colorsDict && this.colorsDict['International Equity']) {
        color = this.colorsDict['International Equity'];
      }

      this.addFieldName(field.label, ProposalStyleClassNames.ProposalTableContent, color);
      field.cellType = PercentageCell;
      field.key = 'International Equity';
      this.addFieldDAO(itemDAO, field);
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (out && this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }
    return out;
  }
}
